import React, { useState } from "react";
import axios from "axios";
import API_ENDPOINTS from '../../utils/apiEndpoints';
import { useNavigate } from "react-router-dom";

const ChangeParentPassword = () => {
    const [identifier, setIdentifier] = useState("");
    const [parentDetails, setParentDetails] = useState(null);
    const [step, setStep] = useState(1); // 1 = Lookup, 2 = Confirm & Reveal, 3 = Reset Form

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState(false);
    const navigate = useNavigate();

    const handleFetchDetails = async () => {
        setMessage("");
        setIsError(false);
        setParentDetails(null);

        if (!identifier.trim()) {
            setMessage("Please enter a mobile number or Username.");
            setIsError(true);
            return;
        }

        try {
            const token = localStorage.getItem("adminToken");
            const response = await axios.post(`${API_ENDPOINTS.ADMIN_PARENT_DETAILS}`,
                { identifier: identifier },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setParentDetails(response.data.data);
                setStep(2);
                setMessage("Parent found.");
                setIsError(false);
            } else {
                setMessage(response.data.message || "Parent not found.");
                setIsError(true);
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Error fetching parent details.");
            setIsError(true);
        }
    };

    const handleProceed = () => {
        setStep(3);
        setMessage("");
    };

    const handleChangePassword = async () => {
        setMessage("");
        setIsError(false);
        setPasswordChanged(false);

        if (!newPassword || newPassword !== confirmPassword) {
            setMessage("Passwords do not match or are empty.");
            setIsError(true);
            return;
        }

        try {
            const token = localStorage.getItem("adminToken");
            const response = await axios.post(API_ENDPOINTS.ADMIN_PARENT_PASS_CHANGE,
                {
                    parent_id: parentDetails.id,
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setMessage(response.data.message || "Password changed successfully.");
            setNewPassword("");
            setConfirmPassword("");
            setPasswordChanged(true);
            setIsError(false);
        } catch (err) {
            setMessage(err.response?.data?.message || "Error changing password.");
            setIsError(true);
        }
    };

    const handleGoBack = () => {
        navigate("/admin/dashboard");
    };

    return (
        <div className="w-screen min-h-screen flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8 py-10">
            <div className="relative w-full max-w-md mx-auto">
                <div className="px-8 py-6 text-left bg-white rounded-xl shadow-lg border border-gray-100">
                    <div className="flex flex-col justify-center items-center h-full">
                        <div className="flex flex-col items-center justify-center gap-2 mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Change Parent Password</h2>
                            <p className="text-xs text-center text-gray-500">Search for a parent and reset their password.</p>
                        </div>

                        {message && (
                            <div className={`w-full p-3 mb-4 text-sm rounded-lg ${isError ? "bg-red-50 text-red-600 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
                                {message}
                            </div>
                        )}

                        <div className="w-full flex flex-col gap-4">

                            {/* Step 1: Lookup */}
                            {step === 1 && (
                                <>
                                    <div>
                                        <label className="font-semibold text-xs text-gray-600 mb-1 block">Parent Mobile or Username</label>
                                        <input
                                            className="border rounded-lg px-4 py-3 text-sm w-full outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            type="text"
                                            placeholder="Enter registered mobile or Username"
                                            value={identifier}
                                            onChange={(e) => setIdentifier(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFetchDetails()}
                                        />
                                    </div>
                                    <button
                                        onClick={handleFetchDetails}
                                        className="mt-2 py-3 px-8 bg-blue-600 hover:bg-blue-700 text-white w-full transition ease-in duration-200 text-center font-semibold rounded-lg shadow-md focus:outline-none"
                                    >
                                        Fetch Details
                                    </button>
                                </>
                            )}

                            {/* Step 2: Confirm Details */}
                            {step === 2 && parentDetails && (
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 w-full">
                                    <h3 className="text-sm font-bold text-gray-700 border-b pb-2 mb-3">Parent Details Found</h3>
                                    <div className="flex flex-col gap-2 text-sm text-gray-600">
                                        <p><span className="font-semibold">Name:</span> {parentDetails.name}</p>
                                        <p><span className="font-semibold">Mobile:</span> {parentDetails.mobile}</p>
                                        <p><span className="font-semibold">Username:</span> {parentDetails.email}</p>
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={() => { setStep(1); setParentDetails(null); setMessage(""); }}
                                            className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 transition rounded-lg font-semibold text-sm"
                                        >
                                            Wrong Parent
                                        </button>
                                        <button
                                            onClick={handleProceed}
                                            className="flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white transition rounded-lg font-semibold text-sm shadow-md"
                                        >
                                            Proceed
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Reset Password Form */}
                            {step === 3 && (
                                <div className="flex flex-col gap-3">
                                    <div className="bg-blue-50 border border-blue-100 text-blue-700 p-3 rounded-lg text-xs mb-2">
                                        <span className="font-semibold">Resetting password for: </span> {parentDetails?.name}
                                    </div>

                                    <div>
                                        <label className="font-semibold text-xs text-gray-600 mb-1 block">New Password</label>
                                        <input
                                            className="border rounded-lg px-4 py-3 text-sm w-full outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            type="password"
                                            placeholder="Enter new password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>

                                    <div>
                                        <label className="font-semibold text-xs text-gray-600 mb-1 block">Confirm New Password</label>
                                        <input
                                            className="border rounded-lg px-4 py-3 text-sm w-full outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            type="password"
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </div>

                                    {!passwordChanged ? (
                                        <div className="flex gap-3 mt-4">
                                            <button
                                                onClick={() => setStep(2)}
                                                className="py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 transition rounded-lg font-semibold shadow-sm w-1/3"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleChangePassword}
                                                className="py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white w-2/3 transition duration-200 text-center font-semibold shadow-md rounded-lg"
                                            >
                                                Change Password
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleGoBack}
                                            className="mt-4 py-3 px-8 bg-gray-800 hover:bg-black text-white transition duration-200 text-center font-semibold shadow-md rounded-lg"
                                        >
                                            Return to Dashboard
                                        </button>
                                    )}
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeParentPassword;
